import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import {
  Description as DocumentIcon,
  Delete as DeleteIcon,
  FindInPage as FindInPageIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { PdfTemplate } from '../../../../../types';

interface TemplateCardProps {
  template: PdfTemplate;
  document: any;
  fields: string[];
  extractFields: (documentFileId: string, templateId?: string) => void;
  deleteTemplate: (templateId: number) => void;
  showSnackbar: (message: string, severity?: 'success' | 'error') => void;
  extractingFields: boolean;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  document,
  fields,
  extractFields,
  deleteTemplate,
  showSnackbar,
  extractingFields,
}) => {
  return (
    <Box sx={{ width: { xs: '100%', md: '48%', lg: '32%' } }}>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <DocumentIcon color="primary" />
            <Typography variant="subtitle1" noWrap sx={{ flexGrow: 1 }}>
              {document?.fileName || 'Unknown document'}
            </Typography>
            <Tooltip title="Extract Fields">
              <IconButton 
                size="small" 
                onClick={() => extractFields(template.documentFileId, template.templateId)}
                disabled={extractingFields}
              >
                <FindInPageIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Template">
              <IconButton 
                size="small" 
                color="error" 
                onClick={() => deleteTemplate(template.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Mappable Fields:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {fields.length > 0 ? (
                fields.map((field, index) => (
                  <Chip 
                    key={index} 
                    label={field} 
                    size="small" 
                    variant="outlined"
                    icon={<ContentCopyIcon fontSize="small" />}
                    onClick={() => {
                      navigator.clipboard.writeText(field);
                      showSnackbar('Field name copied to clipboard');
                    }}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No fields extracted yet
                </Typography>
              )}
            </Box>
          </Box>
          
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            ID: {template.templateId}
          </Typography>
          <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
            Last modified: {new Date(template.modifiedDate).toLocaleString()} by {template.modifiedBy}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TemplateCard;
